// @ts-check

import { useArrayFilter, useArrayReduce } from '@vueuse/core'
import * as _ from 'lodash-es'
import { computed } from 'vue'

/**
 * Composable for managing query param filters.
 * @param {import('vue').Ref<ManagedFilterItem[]>} filters
 * @param {import('vue-router').RouteLocationNormalizedLoaded} route
 */
export const useFilters = (filters, route) => {
  const query = computed(() => route.query)

  /**
   * Computed for query parameters without the managed filters.
   */
  const queryWithoutFilters = useArrayReduce(
    filters,
    /** @returns {any} */
    (accQuery, filter) => ({
      ...accQuery,
      [filter.param]: filter.isArray ? [] : undefined
    }),
    query
  )

  /**
   * Returns query parameters with the given filters added.
   * @param {ManagedFilterItem[]} filtersToAdd
   */
  const getQueryWithFilters = (filtersToAdd) =>
    filtersToAdd.reduce(
      (accQuery, filter) => ({
        ...accQuery,
        [filter.param]: filter.isArray
          ? _.concat(accQuery[filter.param], filter.value)
          : filter.value
      }),
      queryWithoutFilters.value
    )

  /**
   * Computed for query parameters with the managed filters.
   */
  const queryWithFilters = computed(() => getQueryWithFilters(filters.value))

  /**
   * Computed for the current filters in the route query.
   */
  const selectedFilters = useArrayFilter(filters, (filter) =>
    filter.isArray
      ? _.includes(query.value[filter.param], filter.value)
      : query.value[filter.param] === filter.value
  )

  return {
    selectedFilters,
    queryWithoutFilters,
    queryWithFilters,
    getQueryWithFilters
  }
}

export default useFilters
